import React, { useEffect, useRef, useState } from 'react';
import { PageStyled } from './index.styles';
import { useMediaScreen } from 'common/theme/media';
import VoteNumber from 'components/vote-number';
import Icon from '@mui/material/Icon';
import { ChevronRight } from '@mui/icons-material';
import { Box, Button, Link, Rating, Skeleton } from '@mui/material';
import { convertToSlug } from 'common/utils';
import LazyImage from 'components/lazy-image';
import { routesPaths } from 'config/routes';
import i18n from 'assets/i18n';
import { t } from 'i18next';

interface HotelCityTicketCardProps {
    data?: any;
}

const HotelCityTicketCard: React.FC<HotelCityTicketCardProps> = ({ data }) => {
    const { mediaType } = useMediaScreen();
    const isMobil = mediaType === 'xltablet' || mediaType === 'tablet' || mediaType === 'mobile';
    const elementRef = useRef<HTMLDivElement | null>(null);
    const [isVisible, setVisible] = useState(false);

    const url = data
        ? `${routesPaths[i18n.language].hotelDetails}/${convertToSlug(data.name)}-F${data.facility_reference}`
        : '';

    useEffect(() => {
        const observer = new IntersectionObserver((enteries) => {
            enteries.forEach((entery: IntersectionObserverEntry) => {
                setVisible(entery.isIntersecting);
            });
        });
        elementRef.current && observer.observe(elementRef.current);
        return () => {
            observer.disconnect();
        };
    }, [elementRef.current]);

    const WebTicketCard = () => {
        return data ? (
            <div className="ticket-card web">
                <div className="column-1">
                    <Button href={url} className="thumbnail">
                        {data.pension_label && (
                            <div className="thumbnail-badge">
                                <Icon>check_circle_filled</Icon> {data.pension_label}
                            </div>
                        )}
                        <LazyImage
                            src={`${data.main_image_url}`}
                            alt={`${data.name}`}
                            fallbackSrc="/assets/images/loading.gif"
                        />
                    </Button>
                </div>
                <div className="column-2">
                    <div className="ticket-title">
                        <Link href={url} underline="none" color="inherit">
                            {data.name}
                        </Link>
                        {data.star_number > 0 && (
                            <Rating
                                className="ticket-rating"
                                max={Math.floor(data.star_number)}
                                name="read-only"
                                size="small"
                                value={Math.floor(data.star_number)}
                                readOnly
                            />
                        )}
                    </div>
                    {data.location && (
                        <div className="location">
                            <Box display="flex" alignItems="center" gap="5px">
                                <Icon>map</Icon>
                                <span>{data.location}</span>
                            </Box>
                        </div>
                    )}
                    <div
                        className="summary"
                        dangerouslySetInnerHTML={{
                            __html: data.summary,
                        }}
                    ></div>
                </div>
                <div className="column-3">
                    <div className="votes">
                        <VoteNumber vote={data.survey_score} color="secondary" size="small" voteAlign="right" />
                    </div>
                    <div className="bottom-box">
                        <div className="buttons">
                            <Button
                                href={url}
                                variant="contained"
                                color="primary"
                                endIcon={<ChevronRight />}
                                onClick={(e: any) => {
                                    console.log('e', e);

                                    e.stopPropagation();
                                }}
                            >
                                {t('button.avalibility')}
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        ) : (
            <WebSkeleton />
        );
    };
    const MobilTicketCard = () => {
        return data ? (
            <div className="ticket-card mobil">
                <div className="column-1">
                    <Button href={url} className="thumbnail">
                        <LazyImage
                            src={`${data.main_image_url}`}
                            alt={`${data.name}`}
                            fallbackSrc="/assets/images/loading.gif"
                        />
                    </Button>
                </div>
                <div className="column-2">
                    <div className="ticket-title">
                        <Link href={url} underline="none" color="inherit" className="title">
                            {data.name}
                        </Link>
                        {data.star_number > 0 && (
                            <Rating
                                className="ticket-rating"
                                max={Math.floor(data.star_number)}
                                name="read-only"
                                size="small"
                                value={Math.floor(data.star_number)}
                                readOnly
                            />
                        )}
                    </div>
                </div>
                <div className="column-3">
                    {data.location && (
                        <div className="location">
                            <Box display="flex" alignItems="center" gap="5px">
                                <Icon>map</Icon>
                                <span>{data.location}</span>
                            </Box>
                        </div>
                    )}
                    <div
                        className="summary"
                        dangerouslySetInnerHTML={{
                            __html: data.summary,
                        }}
                    ></div>
                </div>

                <div className="column-5">
                    <div className="votes">
                        <VoteNumber vote={data.survey_score} color="secondary" size="small" />
                    </div>
                    <div className="buttons">
                        <Button
                            href={url}
                            fullWidth
                            variant="contained"
                            color="primary"
                            endIcon={<ChevronRight />}
                            onClick={(e: any) => {
                                console.log('e', e);

                                e.stopPropagation();
                            }}
                        >
                            {t('button.avalibility')}
                        </Button>
                    </div>
                </div>
            </div>
        ) : (
            <MobilSkeleton />
        );
    };
    const WebSkeleton = () => {
        return (
            <div className="ticket-card web">
                <div className="column-1">
                    <Skeleton animation="wave" variant="rectangular" width={230} height={230} />
                </div>
                <div className="column-2">
                    <Skeleton animation="wave" width={'70%'} height={25} />
                    <div className="location">
                        <Box display="flex" alignItems="center" gap="5px">
                            <Skeleton animation="wave" width={'80%'} height={25} />
                        </Box>
                    </div>
                    <div className="location-km">
                        <Box display="flex" alignItems="center" gap="5px">
                            <Skeleton animation="wave" width={'50%'} height={25} />
                        </Box>
                    </div>
                    <div className="">
                        <Skeleton animation="wave" width={'25%'} height={25} />
                    </div>
                    <Box display="flex" alignItems="center" gap="5px">
                        <Skeleton animation="wave" variant="circular" width={25} height={25} />
                        <Skeleton animation="wave" variant="circular" width={25} height={25} />
                        <Skeleton animation="wave" variant="circular" width={25} height={25} />
                        <Skeleton animation="wave" variant="circular" width={25} height={25} />
                    </Box>
                    <div className="alert-box">
                        <Skeleton animation="wave" width={50} height={25} />
                        <Skeleton animation="wave" width={50} height={25} />
                    </div>
                </div>
                <div className="column-3">
                    <div className="votes">
                        <Skeleton animation="wave" width={120} height={28} />
                    </div>
                    <div className="bottom-box">
                        <div className="guest-info">
                            <Skeleton animation="wave" width={120} height={36} />
                        </div>

                        <div className="price-box">
                            <div className="ticket-price">
                                <Skeleton animation="wave" width={90} height={28} />
                            </div>
                        </div>
                        <div className="buttons">
                            <Skeleton animation="wave" variant="rounded" width={130} height={36} />
                        </div>
                    </div>
                </div>
            </div>
        );
    };
    const MobilSkeleton = () => {
        return (
            <div className="ticket-card mobil">
                <div className="column-1">
                    <Skeleton animation="wave" variant="rectangular" width={'100%'} height={230} />
                </div>

                <div className="column-2">
                    <Skeleton animation="wave" width={'70%'} height={45} />
                    <Skeleton animation="wave" width={45} height={45} />
                </div>
                <div className="column-3">
                    <Skeleton animation="wave" width={'70%'} height={25} />
                    <Skeleton animation="wave" width={'50%'} height={25} />
                </div>
                <div className="column-4">
                    <Skeleton animation="wave" width={'30%'} height={25} />
                    <Skeleton animation="wave" width={'30%'} height={25} />
                </div>
                <div className="column-5">
                    <Skeleton animation="wave" width={'30%'} height={35} />
                    <Skeleton animation="wave" width={'30%'} height={35} />
                </div>
            </div>
        );
    };

    return (
        <PageStyled>
            <div
                className={`ticket ${isMobil ? 'mobil' : 'web'} ${isVisible ? 'fade-in' : ''} ${
                    data ? '' : 'no-animate'
                }`}
                ref={elementRef}
            >
                {isMobil ? <MobilTicketCard /> : <WebTicketCard />}
            </div>
        </PageStyled>
    );
};

export default HotelCityTicketCard;
